import {clone, toPath} from 'lodash';

interface YupError {
    path: string;
    message: string;
    inner?: YupError[];
}

function isObject(obj: any): obj is object {
    return obj !== null && typeof obj === 'object';
}

function isInteger(obj: any): boolean {
    return String(Math.floor(Number(obj))) === obj;
}

function setIn(obj: any, path: string, value: any): any {
    const res = clone(obj); // this keeps inheritance when obj is a class
    let resVal = res;
    let i = 0;
    const pathArray = toPath(path);

    for (; i < pathArray.length - 1; i++) {
        const currentPath = pathArray[i];
        const currentObj = getIn(obj, pathArray.slice(0, i + 1));

        if (currentObj && (isObject(currentObj) || Array.isArray(currentObj))) {
            resVal = resVal[currentPath] = clone(currentObj);
        } else {
            const nextPath = pathArray[i + 1];
            resVal = resVal[currentPath] = isInteger(nextPath) && Number(nextPath) >= 0 ? [] : {};
        }
    }

    // Return original object if new value is the same as current
    if ((i === 0 ? obj : resVal)[pathArray[i]] === value) {
        return obj;
    }

    if (value === undefined) {
        delete resVal[pathArray[i]];
    } else {
        resVal[pathArray[i]] = value;
    }

    // If the path array has a single element, the loop did not run.
    // Deleting on `resVal` had no effect in this scenario, so we delete on the result instead.
    if (i === 0 && value === undefined) {
        delete res[pathArray[i]];
    }

    return res;
}

function getIn(obj: any, key: string | string[], def?: any, p: number = 0): any {
    const path = toPath(key);

    while (obj && p < path.length) {
        obj = obj[path[p++]];
    }

    // Check if path is not in the end
    if (p !== path.length && !obj) {
        return def;
    }

    return obj === undefined ? def : obj;
}

export function yupParseErrors(yupError: YupError): Record<string, any> {
    let errors: Record<string, any> = {};

    if (yupError.inner) {
        if (yupError.inner.length === 0) {
            return setIn(errors, yupError.path, yupError.message);
        }
        for (const err of yupError.inner) {
            if (!getIn(errors, err.path)) {
                errors = setIn(errors, err.path, err.message);
            }
        }
    }

    return errors;
}